/**
 * Axios API client for the WorkRight backend.
 *
 * Contract (see backend/internal/handlers + middleware):
 *  - Auth is COOKIE-ONLY. The backend sets three cookies:
 *      wr_access  (httpOnly)            — JWT access token
 *      wr_refresh (httpOnly, /api/v1/auth scope) — rotating refresh token
 *      wr_csrf    (readable)            — CSRF double-submit token
 *    We must NEVER read/write a JWT in localStorage/sessionStorage.
 *  - Every request sends cookies (`withCredentials: true`).
 *  - State-changing requests (POST/PUT/PATCH/DELETE) must echo the
 *    CSRF token in the `X-CSRF-Token` header (middleware/csrf.go
 *    double-submit check). The cookie is host-only on the API, so the
 *    token is taken from response bodies (see the CSRF store below).
 *  - On 401 we attempt exactly ONE refresh (POST /api/v1/auth/refresh,
 *    which rotates the refresh token) and retry the original request.
 *    A second failure redirects to /login. No infinite loops.
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiError } from "@/types/auth";
import { safeNext } from "@/lib/auth/safe-next";
import { isCredentialFailure } from "./auth-retry";
import { humanizeApiMessage } from "./humanize";
import { builtInHosts, consoleOnlyTarget } from "../console-host";
import {
  peerRefreshedSince,
  refreshCoordination,
  REFRESH_TIMEOUT_MS,
} from "./refresh-coordination";
import {
  requestStepUp,
  shouldStepUp,
  STEP_UP_CANCELLED,
  STEP_UP_CANCELLED_MESSAGE,
} from "./step-up";

export { setStepUpRequester } from "./step-up";

// Cookie/header names — must match backend/internal/middleware/auth.go.
export const CSRF_COOKIE = "wr_csrf";
export const CSRF_HEADER = "X-CSRF-Token";

// In-memory CSRF token store. The backend's auth cookies are HOST-ONLY on
// the API (security review 2026-10-20 H2 — no `.workright.in` Domain), so
// in production (app.workright.in calling api.workright.in) the `wr_csrf`
// cookie is unreadable from `document.cookie`. The token comes from
// response bodies instead: `csrf_token` on login / register / activate /
// 2FA verify, and on every GET /me (adoptCSRFFromMe) — so a page load or a
// new tab learns it from the boot-time /me, and a 403 csrf re-learns it
// once (refreshCSRFToken). The stored token is AUTHORITATIVE: the only
// `wr_csrf` cookie the dashboard can read in production is a legacy
// `Domain=.workright.in` copy or one planted by a sibling subdomain, and
// preferring it would pin every write to a 403. The cookie is read only when
// nothing is stored (local dev through the Vite /api proxy, same origin).
// Persisted to sessionStorage so a page refresh doesn't lose the value.
const CSRF_STORAGE_KEY = "wr_csrf_memory";
let csrfMemory: string | null = null;

export function setCSRFToken(token: string | null | undefined): void {
  if (!token) return;
  csrfMemory = token;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    } catch {
      // sessionStorage may throw in incognito / quota-exceeded; the
      // in-memory copy still works for the current page lifetime.
    }
  }
}

export function clearCSRFToken(): void {
  csrfMemory = null;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
    } catch {
      // best effort
    }
  }
}

function readCSRFFromStore(): string | null {
  if (csrfMemory) return csrfMemory;
  if (typeof window === "undefined") return null;
  try {
    csrfMemory = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
    return csrfMemory;
  } catch {
    return null;
  }
}

// API origin. The backend mounts everything under /api/v1; we keep that
// prefix in each call site so paths read like the router table.
//
// Exported because some surfaces need an absolute URL (CSV downloads
// triggered by an `<a href download>` anchor — the browser resolves
// relative hrefs against the current origin, which is the frontend,
// not the backend, so the CSV button 404s in any deploy where the two
// services live at different origins. See G_ACC8 HIGH-1 audit fix.)
// Migration note (2026-06-01, Next.js → Vite): the Next.js sibling at
// frontend/src/lib/api/client.ts reads
// `process.env.NEXT_PUBLIC_API_URL` — baked at next build time. Vite's
// equivalent is `import.meta.env.VITE_API_URL` (bundle-baked too).
//
// Dev fallback (2026-06-02 fix): empty string, so axios issues
// SAME-ORIGIN requests. The Vite dev server's /api/* proxy
// (vite.config.ts) then forwards to localhost:8090 transparently.
// This dodges CORS preflight entirely in dev — the cross-origin
// hard-coded fallback we had before was getting blocked by the
// backend's allow-list (which doesn't include localhost:3001) and
// every browser-side API call from the dev SPA failed at the
// preflight stage. The Audit 1 walkthrough was contaminated by this.
//
// In production builds, VITE_API_URL is always set (baked at build
// time by the CF Pages build env), so this fallback never runs.
export const API_BASE_URL =
  import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";

/**
 * DOM event fired on a 402 `employee_limit_reached` (Free headcount cap).
 * EmployeeLimitDialog (mounted once in the workspace layout) listens and
 * shows an upgrade dialog; every add-employee surface (single add, bulk
 * import, ATS hire, reactivation) therefore leads to billing without
 * per-call plumbing. The original error still rejects so callers can
 * keep their own inline handling.
 */
export const EMPLOYEE_LIMIT_EVENT = "wr:employee-limit-reached";

export interface EmployeeLimitDetail {
  /** Plain refusal, e.g. "Your Free plan covers 5 people. …" */
  message?: string;
  limit?: number;
  current_count?: number;
  available?: number;
  over_limit_rows?: number[];
  upgrade_hint?: string;
  /**
   * What lifts the cap: "upgrade" (Free), "add_packs" (Pro at its bought
   * packs), "renew" (Pro ended), "posh_pay" (POSH only, POSH not paid or
   * ended), "posh_band" (the people the bought POSH covers are all in).
   */
  cta?: "upgrade" | "add_packs" | "renew" | "posh_pay" | "posh_band";
}

const REFRESH_PATH = "/api/v1/auth/refresh";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

/** Read a readable (non-httpOnly) cookie by name. */
export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * The CSRF token an unsafe request should carry right now: the stored
 * token (response bodies / GET /me), else the readable `wr_csrf` cookie
 * (local dev via the Vite proxy). The axios interceptor below uses it;
 * exported for the rare request that cannot go through axios — a
 * `fetch(..., { keepalive: true })` sent while the page is being closed.
 */
export function currentCSRFToken(): string | null {
  return readCSRFFromStore() ?? readCookie(CSRF_COOKIE);
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// --- Request interceptor: inject CSRF header on unsafe methods. --------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // When this attempt went out: a 401 for it is stale once another tab (or
  // an earlier request of this one) has refreshed SINCE — see
  // coalescedRefresh / refresh-coordination.ts.
  (config as InternalAxiosRequestConfig & RetriableConfig)._sentAt =
    refreshCoordination().now();
  const method = (config.method ?? "get").toLowerCase();
  if (UNSAFE_METHODS.has(method)) {
    // A 403 csrf replay carries the token /me just returned (never the
    // cookie). Otherwise the stored token from a response body (login /
    // activate / GET /me); the readable wr_csrf cookie only when nothing is
    // stored (local dev via the Vite proxy) — see the CSRF store note.
    const token =
      (config as InternalAxiosRequestConfig & RetriableConfig)._csrfToken ??
      currentCSRFToken();
    if (token) {
      config.headers.set(CSRF_HEADER, token);
    }
  }
  return config;
});

// --- Response interceptor: single refresh-then-retry on 401. ----------
//
// We coalesce concurrent 401s onto a single in-flight refresh so we
// never hammer the rotating-refresh endpoint. A burst of parallel
// requests must NOT each rotate the refresh token: the backend treats a
// reuse of an already-rotated token as theft (refresh_reuse), revokes
// every session, and 401s. So coalescing is a security property, not
// just an optimisation.
//
// Coalescing is gated on an epoch counter, not merely a nullable
// in-flight promise. A 401'd request captures the epoch it observed.
// After awaiting the shared refresh it retries once against the new
// cookie. If that retry also 401s, the epoch has moved on, so the
// request does NOT start a *second* refresh (which would rotate the
// token again and trip refresh_reuse) — it just fails out to /login.
// Only a *failed* refresh re-arms the gate so a genuinely new session
// can refresh later.
//
// Across TABS the gate is refresh-coordination.ts: an origin-wide Web Lock
// serialises the refresh POSTs of every tab, and a tab that waited for it
// skips its own rotation when a peer refreshed after its failed request went
// out (`_sentAt`) — it just replays against the cookie the peer obtained.

export type RetriableConfig = AxiosRequestConfig & {
  _retried?: boolean;
  /** Replayed once after an in-place step-up 2FA (never prompts twice). */
  _stepUpReplayed?: boolean;
  /** Replayed once after re-learning the CSRF token from /me. */
  _csrfRetried?: boolean;
  /** The token the CSRF replay must send (fresh from /me, never the cookie). */
  _csrfToken?: string;
  /**
   * Set by the mount-time /me probe (and the refresh POST itself). On 401
   * the interceptor never NAVIGATES — it rejects quietly, so AuthProvider
   * sets user=null for an anonymous visitor (e.g. on /register, /forgot)
   * instead of bouncing them to /login?next=/register. It still tries ONE
   * shared refresh first and replays once: a reload after the access cookie
   * expired must recover the session the refresh cookie still holds
   * (2026-10 re-test), not land on /login.
   */
  _suppressAuthRedirect?: boolean;
  /** When this attempt was sent (refresh-coordination clock). */
  _sentAt?: number;
};

let refreshInFlight: Promise<void> | null = null;
// Bumped every time a refresh attempt settles. A request remembers the
// epoch it saw at 401 time; if the epoch advanced while it waited, the
// shared refresh already ran (success or failure) and it must not kick
// off another one.
let refreshEpoch = 0;

// Idempotent hard-redirect guard: at most one window.location.assign
// per logical auth failure, even though many coalesced requests reject
// at once. Never reset within a tick — the page is navigating away.
let redirecting = false;

/**
 * Single owner of the post-auth-failure hard navigation. `reason`, when
 * given, is surfaced on the login page (e.g. session_revoked after a
 * refresh_reuse). The `next` target is derived from the *current*
 * same-origin location (browser-provided, not request input) and run
 * through safeNext()/encodeURIComponent, so it cannot be steered to an
 * off-origin URL — see client.ts open-redirect note below.
 */
function redirectToLogin(reason?: "session_revoked") {
  if (typeof window === "undefined") return;
  if (redirecting) return;
  const { pathname, search } = window.location;
  if (pathname.startsWith("/login")) {
    redirecting = true;
    if (reason) window.location.assign(`/login?reason=${reason}`);
    return;
  }
  redirecting = true;
  // pathname+search is the browser's own current URL, never a value
  // taken from a response/request. safeNext() still defends against a
  // crafted current path (e.g. //evil if somehow navigated there) and
  // encodeURIComponent keeps it a single query param value.
  const next = encodeURIComponent(safeNext(pathname + search));
  const reasonQ = reason ? `&reason=${reason}` : "";
  window.location.assign(`/login?next=${next}${reasonQ}`);
}

/**
 * Recover the active tenant's URL slug from the browser's current path.
 * Every authenticated route lives under `/app/{tenantSlug}/...`, so the
 * slug is the first segment after `/app/`. Returns "" when the current
 * URL isn't an in-app `/app/<slug>/...` route (e.g. /login, /2fa-setup,
 * or a bare `/app`), in which case the caller must NOT build an
 * `/app//settings/billing` URL. Browser-provided input only — never a
 * value from a request/response.
 */
function activeTenantSlugFromPath(): string {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(/^\/app\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

function isRefreshReuse(error: AxiosError<ApiError>): boolean {
  return error.response?.data?.error === "refresh_reuse";
}

/**
 * One refresh POST, serialised across this origin's tabs. `staleSince` is
 * when the request that 401'd was sent: if a peer tab refreshed after that,
 * the browser already holds the rotated cookies and rotating again is
 * pointless (and, had both POSTs been in flight together, the second would
 * have presented a used token → refresh_reuse → every session revoked).
 */
async function runRefresh(staleSince: number): Promise<void> {
  const tabs = refreshCoordination();
  await tabs.withLock(async () => {
    if (peerRefreshedSince(staleSince, tabs.lastRefreshedAt(), tabs.now())) {
      return;
    }
    // The refresh cookie is httpOnly and scoped to /api/v1/auth; the
    // browser attaches it automatically because of withCredentials.
    // _retried marks it so a 401 here can't recurse into another refresh;
    // _suppressAuthRedirect leaves the navigation to the request that
    // needed the refresh (the boot probe must not navigate at all). The
    // timeout bounds how long the cross-tab lock can be held.
    await api.post(REFRESH_PATH, null, {
      _retried: true,
      _suppressAuthRedirect: true,
      timeout: REFRESH_TIMEOUT_MS,
    } as RetriableConfig);
    tabs.markRefreshed(tabs.now());
  });
}

/**
 * Coalesced, epoch-gated refresh.
 *
 * `seenEpoch` is the refresh generation the calling request's stale
 * cookie belonged to when it 401'd. Behaviour:
 *  - a refresh is already in flight  → await it (pure coalescing).
 *  - no refresh in flight AND the epoch hasn't moved since this request
 *    saw its 401 → start exactly one refresh (rotates the token once).
 *  - no refresh in flight BUT the epoch already advanced → a peer's
 *    refresh completed after our cookie went stale; do NOT start a
 *    second rotation (would trip backend refresh_reuse). Resolve so the
 *    caller retries once against the cookie the peer already obtained.
 * Every settle (success or failure) advances the epoch and clears the
 * in-flight slot, so a failed refresh re-arms the gate for a future
 * legitimately-new session.
 */
async function coalescedRefresh(seenEpoch: number, staleSince: number): Promise<void> {
  const inFlight = refreshInFlight;
  if (inFlight !== null) {
    await inFlight;
    return;
  }
  if (refreshEpoch !== seenEpoch) {
    // A newer cookie already exists — skip refresh, let caller retry.
    return;
  }
  const started = runRefresh(staleSince)
    .finally(() => {
      // Advance the epoch on EVERY settle. On success this blocks a
      // second token-rotating refresh from stale-cookie 401s; on
      // failure it re-arms the gate for a future new session.
      refreshEpoch += 1;
      refreshInFlight = null;
    });
  refreshInFlight = started;
  await started;
}

// enrolmentProbe coalesces the /me probe of concurrent 403
// tenant_required responses (a page fires several queries at once).
let enrolmentProbe: Promise<boolean> | null = null;

const ME_PATH = "/api/v1/me";

/** Exported for the unit test. See the success interceptor. */
export function adoptCSRFFromMe(url: string | undefined, data: unknown): void {
  if (!url || url.split("?")[0] !== ME_PATH) return;
  if (!data || typeof data !== "object") return;
  const body = data as { csrf_token?: unknown; role?: unknown };
  // The role decides the upgrade_required redirect (only a buyer goes to
  // Billing); /me is where the client learns it.
  lastMeRole = typeof body.role === "string" ? body.role : null;
  if (typeof body.csrf_token === "string" && body.csrf_token !== "") {
    setCSRFToken(body.csrf_token);
    lastMeCSRFToken = body.csrf_token;
  }
  delete body.csrf_token;
}

// The role the most recent GET /me returned (null before the first /me).
let lastMeRole: string | null = null;

/**
 * Whether an upgrade_required refusal sends this person to Billing: only
 * someone who can buy a plan (the company admin). Anyone else — an ICC
 * member opening a POSH link while POSH is off — stays on the page, whose
 * feature gate says the module isn't on and to ask the admin; Billing
 * would only show them "Only your company admin can change the
 * subscription" (ICC QA, 28 Oct 2026).
 */
export function upgradeGoesToBilling(role: string | null | undefined): boolean {
  return role === "company_admin" || role === "super_admin";
}

// The csrf_token the most recent GET /me returned (adoptCSRFFromMe strips
// it from the payload before callers see it, so refreshCSRFToken reads it
// here).
let lastMeCSRFToken: string | null = null;

let csrfProbe: Promise<string | null> | null = null;

/** Re-learn the session's CSRF token from GET /me (the success interceptor
 *  adopts it). Concurrent callers share one request. Resolves the fresh
 *  token, or null when /me fails or returns no token (no membership
 *  session) — never a cookie value, which may be a planted or legacy copy.
 *  The 403 came from the CSRF check, before any handler ran, so one replay
 *  is safe. */
function refreshCSRFToken(): Promise<string | null> {
  if (!csrfProbe) {
    lastMeCSRFToken = null;
    csrfProbe = api
      .get<{ csrf_token?: string }>(ME_PATH, { _suppressAuthRedirect: true } as RetriableConfig)
      .then(() => lastMeCSRFToken)
      .catch(() => null)
      .finally(() => {
        csrfProbe = null;
      });
  }
  return csrfProbe;
}

/** Whether /me now reports an enrolment-only session (2FA mandatory, no
 *  method yet). False on any error — the caller then does nothing. */
function enrolmentRequiredNow(): Promise<boolean> {
  if (!enrolmentProbe) {
    enrolmentProbe = api
      .get<{ enrollment_required?: boolean }>("/api/v1/me", {
        _suppressAuthRedirect: true,
      } as RetriableConfig)
      .then((r) => r.data?.enrollment_required === true)
      .catch(() => false)
      .finally(() => {
        enrolmentProbe = null;
      });
  }
  return enrolmentProbe;
}

api.interceptors.response.use(
  (response) => {
    // GET /me carries the session's CSRF token (backend meResponse
    // .csrf_token): the wr_csrf cookie is host-only on the API, so a page
    // load or a new tab learns it here. Adopt it and drop it from the
    // payload, so it never lands in React state or the query cache.
    adoptCSRFFromMe(response.config?.url, response.data);
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const status = error.response?.status;
    const original = error.config as
      | (InternalAxiosRequestConfig & RetriableConfig)
      | undefined;

    // --- csrf (403): this tab's CSRF token is stale (another tab signed in
    // again or switched company, so the session's token changed) or was
    // never learned. Ask /me for the session's current token once (shared
    // by concurrent 403s) and replay the write once.
    if (
      status === 403 &&
      error.response?.data?.error === "csrf" &&
      original &&
      !original._csrfRetried &&
      UNSAFE_METHODS.has((original.method ?? "get").toLowerCase())
    ) {
      original._csrfRetried = true;
      const fresh = await refreshCSRFToken();
      if (fresh) {
        original._csrfToken = fresh;
        return api(original);
      }
      return Promise.reject(error);
    }

    // --- console_only (403): a super_admin / support session used from the
    // customer app. Move to the console address (lib/console-host).
    if (
      status === 403 &&
      (error.response?.data as { error?: string } | undefined)?.error === "console_only" &&
      typeof window !== "undefined"
    ) {
      const target = consoleOnlyTarget(window.location.origin, window.location.pathname, window.location.search, builtInHosts());
      if (target) {
        window.location.replace(target);
        return new Promise(() => {});
      }
    }

    // --- employee_limit_reached (402): Free headcount cap → upgrade dialog
    if (
      status === 402 &&
      (error.response?.data as { error?: string } | undefined)?.error ===
        "employee_limit_reached" &&
      typeof window !== "undefined"
    ) {
      try {
        window.dispatchEvent(
          new CustomEvent<EmployeeLimitDetail>(EMPLOYEE_LIMIT_EVENT, {
            detail: (error.response?.data ?? {}) as EmployeeLimitDetail,
          }),
        );
      } catch {
        // CustomEvent unavailable — callers' own toast still shows.
      }
      return Promise.reject(error);
    }

    // --- upgrade_required: tenant lacks the feature → in-app Billing --
    // The tenant's plan/feature_overrides don't include the feature this
    // route needs. Route the user to the IN-SPA billing surface
    // (/app/<slug>/settings/billing — the plan picker), NOT the marketing
    // /pricing page: /pricing is a Next.js marketing route, so inside the
    // dashboard SPA it renders "Page not found" (B-A3). The active slug is
    // the first path segment after /app/ in the browser's own current URL.
    // If we can't recover a clean slug (e.g. hit before the workspace
    // layout mounted), we reject quietly rather than bounce to /pricing —
    // the feature-gate components already cover the page-load path with an
    // in-SPA "Upgrade to unlock" panel.
    if (
      status === 403 &&
      error.response?.data?.error === "upgrade_required" &&
      typeof window !== "undefined"
    ) {
      const slug = activeTenantSlugFromPath();
      const target = slug ? `/app/${encodeURIComponent(slug)}/settings/billing` : null;
      // Never from the Billing page itself: a stray out-of-plan request
      // there (POSH only, migration 162) would otherwise reload it forever.
      // Only someone who can buy goes there (upgradeGoesToBilling).
      if (
        upgradeGoesToBilling(lastMeRole) &&
        target &&
        !redirecting &&
        !window.location.pathname.startsWith(target)
      ) {
        redirecting = true;
        window.location.assign(target);
      }
      return Promise.reject(error);
    }

    // --- access_restricted: a leaver past their last working day (wave-2
    // offboarding, backend LeaverAccessGate). Only their own payslips,
    // documents, letters and F&F statement still answer — all on
    // /me/letters. Send the browser there once (never from that page
    // itself, so the shell's other requests can't loop it).
    if (
      status === 403 &&
      error.response?.data?.error === "access_restricted" &&
      typeof window !== "undefined"
    ) {
      const slug = activeTenantSlugFromPath();
      const target = slug ? `/app/${encodeURIComponent(slug)}/me/letters` : null;
      if (target && !redirecting && !window.location.pathname.startsWith(target)) {
        redirecting = true;
        window.location.assign(target);
      }
      return Promise.reject(error);
    }

    // --- tenant_required inside the workspace: the session may have been
    // degraded to enrolment-only mid-session (the company switched
    // Require-2FA on, or this person was promoted to a mandatory-2FA role,
    // and they have no method) — RequireAuth then serves an identity-only
    // context, so every tenant route answers 403 tenant_required. Ask /me
    // once (shared by concurrent 403s); only when it says
    // enrollment_required go straight to /2fa-setup?required=1. Anything
    // else (e.g. an inactive company) is left to the caller.
    if (
      status === 403 &&
      error.response?.data?.error === "tenant_required" &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/app/") &&
      !redirecting
    ) {
      const next = window.location.pathname + window.location.search;
      if (await enrolmentRequiredNow()) {
        if (!redirecting) {
          redirecting = true;
          window.location.assign(`/2fa-setup?required=1&next=${encodeURIComponent(next)}`);
        }
      }
      return Promise.reject(error);
    }

    // --- password_change_required: the session is still on an admin-set
    // temporary password (backend PasswordRotationGate, 2026-10). The auth
    // gate normally routes there first; a tab that was already open when
    // the password was reset lands here — send it to /change-password once.
    if (
      status === 403 &&
      error.response?.data?.error === "password_change_required" &&
      typeof window !== "undefined"
    ) {
      if (!redirecting && !window.location.pathname.startsWith("/change-password")) {
        redirecting = true;
        window.location.assign("/change-password");
      }
      return Promise.reject(error);
    }

    if (status !== 401 || !original) {
      return Promise.reject(error);
    }

    // --- totp_required: route the caller has RequireTOTPEnrolledAndVerified
    // and the session holds no step-up within the last 30 minutes (a
    // refresh keeps a step-up only inside that window — the wr_stepup
    // cookie). This is NOT an ordinary expiry: refresh-then-retry would
    // loop. All strict-TOTP routes share this contract:
    // POSH ICC, complaints, offboarding /close, offboarding /compute-ff,
    // and any future RequireTOTPEnrolledAndVerified gate.
    if (error.response?.data?.error === "totp_required") {
      // 2026-10 browser QA payroll #11: ask for the code in place (the
      // StepUpPrompt in the workspace layout) and replay the request once,
      // instead of the page navigating to /2fa-verify and losing the action.
      if (shouldStepUp("totp_required", original._stepUpReplayed === true)) {
        original._stepUpReplayed = true;
        if (await requestStepUp()) {
          return api(original);
        }
        // Cancelled: nothing was done — say so instead of the caller's
        // "/2fa-verify" fallback.
        if (error.response?.data) {
          error.response.data = { ...error.response.data, error: STEP_UP_CANCELLED, message: STEP_UP_CANCELLED_MESSAGE };
        }
      }
      return Promise.reject(error);
    }

    // --- invalid_code / invalid_credentials: the user typed a wrong 2FA
    // code or password. Not an expiry — refresh-then-replay would resend
    // the same wrong value, spending a second lockout attempt per typo,
    // and then bounce to /login. Reject so the form shows it inline.
    if (isCredentialFailure(error.response?.data?.error)) {
      return Promise.reject(error);
    }

    // --- refresh_reuse: token theft detected, all sessions revoked. --
    // Backend 401s with { error: "refresh_reuse" } either on a normal
    // request or on the refresh POST itself. This is a security signal,
    // not an ordinary expiry — surface it instead of swallowing it.
    if (isRefreshReuse(error)) {
      redirectToLogin("session_revoked");
      return Promise.reject(error);
    }

    // A request that must never navigate — the mount-time /me probe, and
    // the refresh POST itself. The probe still tries ONE shared refresh
    // and replays once: reloading a page after the access cookie expired
    // used to land on /login although the refresh cookie was good (2026-10
    // re-test). It is the same coalesced, cross-tab-serialised refresh
    // every request uses, so a burst of reloading tabs rotates the token
    // once. Anything that still fails — an anonymous visitor on /register
    // has no refresh cookie — rejects quietly (user=null).
    if (original._suppressAuthRedirect) {
      if (original._retried || original.url?.includes(REFRESH_PATH)) {
        return Promise.reject(error);
      }
      const probeEpoch = refreshEpoch;
      original._retried = true;
      try {
        await coalescedRefresh(probeEpoch, original._sentAt ?? refreshCoordination().now());
      } catch {
        return Promise.reject(error);
      }
      return api(original);
    }

    // Never try to refresh the refresh call itself, and only retry
    // once. Both guards funnel through the idempotent redirect; we do
    // NOT assign here — redirectToLogin is the sole navigation owner,
    // so one logical failure can't double-redirect.
    const isRefreshCall = original.url?.includes(REFRESH_PATH);
    if (isRefreshCall || original._retried) {
      // A failed refresh POST re-arms the gate so a future,
      // legitimately new session can refresh again.
      if (isRefreshCall) {
        refreshInFlight = null;
        refreshEpoch += 1;
      }
      redirectToLogin();
      return Promise.reject(error);
    }

    // Epoch captured at the moment THIS request observed its 401 — the
    // refresh generation its now-stale cookie belongs to.
    const seenEpoch = refreshEpoch;
    original._retried = true;

    try {
      await coalescedRefresh(seenEpoch, original._sentAt ?? refreshCoordination().now());
    } catch {
      redirectToLogin();
      return Promise.reject(error);
    }

    // Refresh succeeded (ours, a coalesced peer's, or one that landed
    // before we woke up). Replay exactly once. If the replay 401s,
    // _retried is set so we hit the guard above and redirect — we will
    // NOT rotate the refresh token a second time.
    return api(original);
  },
);

/**
 * Blob-response error rehydration.
 *
 * When a request uses `responseType: "blob"` (file/stream downloads),
 * axios decodes the response body as a Blob even on a 4xx/5xx. The
 * backend's JSON error envelope `{ error, message, field }` therefore
 * arrives as an UNPARSED Blob in `err.response.data`, so toApiError()
 * sees a non-object body and falls back to the generic
 * "Something went wrong" — masking specific branches like
 * not_found → "This document is not available." (which would otherwise
 * be dead code).
 *
 * This reads the Blob as text, best-effort JSON-parses it, and writes
 * the parsed envelope back onto `err.response.data` IN PLACE, then
 * returns the SAME axios error. Callers `await` this in the api layer
 * and re-reject the returned error so every downstream `toApiError(err)`
 * / `e.error === "not_found"` site keeps working unchanged.
 *
 * Security: this only ever surfaces the backend's own neutral envelope.
 * A forbidden/missing doc is 404 `{ error: "not_found" }` server-side
 * (no 403 existence oracle, no storage key on the wire); if the body is
 * not JSON or has no fields we leave a non-object data so toApiError()
 * yields the neutral generic — we never invent an existence signal.
 *
 * No-op (returns the input untouched) when the body is not a Blob, so
 * it is safe to wrap any download regardless of error shape.
 */
export async function blobErrorToApi(err: unknown): Promise<unknown> {
  if (!axios.isAxiosError(err)) return err;
  const ax = err as AxiosError<unknown>;
  const body = ax.response?.data;
  if (!(body instanceof Blob)) return err;
  try {
    const text = await body.text();
    const parsed: unknown = text ? JSON.parse(text) : null;
    if (parsed && typeof parsed === "object") {
      // Rehydrate in place so the existing toApiError() path (and the
      // not_found branch) reads it exactly as a normal JSON error.
      (ax.response as { data: unknown }).data = parsed;
    }
  } catch {
    // Non-JSON / unreadable body: leave data as-is. toApiError() will
    // fall through to the neutral generic message — no oracle.
  }
  return err;
}

/**
 * Normalize an axios error into the backend's { error, message, field }
 * envelope so callers (and react-hook-form wiring) get a stable shape.
 * The message is made toast-ready (humanizeApiMessage: a capital letter,
 * a full stop, no Go slice brackets) — callers branch on `error`, never
 * on the message text.
 */
export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const data = (err as AxiosError<ApiError>).response?.data;
    if (data && typeof data === "object" && "message" in data) {
      return {
        error: data.error ?? "error",
        message: humanizeApiMessage(data.message) || "Something went wrong.",
        field: data.field || undefined,
      };
    }
    if (err.code === "ERR_NETWORK") {
      return {
        error: "network",
        message: "Cannot reach the server. Check your connection.",
      };
    }
  }
  return { error: "error", message: "Something went wrong" };
}

/**
 * Backend error code for the M-1 mandatory-2FA enrolment gate. 2FA is
 * mandatory for super_admin / hr_manager (CLAUDE.md locked decision);
 * the backend enforces it centrally in completeMembershipLogin
 * (auth.go) across login / activate / register / platform.
 */
export const TOTP_ENROLLMENT_REQUIRED = "totp_enrollment_required";

/**
 * True when a caught error is the M-1 mandatory-2FA enrolment gate:
 * HTTP 403 `{ error: "totp_enrollment_required" }`. The backend has
 * ALREADY set an enrolment-scoped IDENTITY-ONLY session cookie (no
 * tenant, no refresh, no CSRF row — the same fail-closed class as the
 * post-login picker), so the caller must route the user to /2fa-setup
 * to enrol a second factor before any tenant / privileged surface is
 * reachable. The 403 reaches callers intact — the response interceptor
 * only special-cases `upgrade_required` and 401 (see above).
 */
export function isTotpEnrollmentRequired(err: unknown): boolean {
  return toApiError(err).error === TOTP_ENROLLMENT_REQUIRED;
}

/**
 * True when a refusal body says the session is still on an admin-set
 * temporary password (`must_change_password: true` — carried by the 403
 * totp_enrollment_required body). Round 2 T-H1: that password is changed
 * BEFORE any 2FA method can be added.
 */
export function refusalNeedsPasswordChange(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const data = (err as { response?: { data?: { must_change_password?: unknown } } }).response?.data;
  return data?.must_change_password === true;
}
